<template>
  <div class="finance_box">
    <el-form v-loading="state.loading" label-width="120px" class="brand-form">
      <el-form-item :label="t('brandSetting.systemName')">
        <el-input
          v-model="state.form.backendSystemName"
          :placeholder="t('brandSetting.systemNamePh')"
          @input="onBrandTextChange"
        />
      </el-form-item>
      <el-form-item :label="t('brandSetting.logo')">
        <el-upload
          v-model:file-list="state.logoList"
          :action="state.uploadUrl"
          :headers="state.headers"
          list-type="picture-card"
          :limit="1"
          :accept="'image/*'"
          :before-upload="beforeUpload"
          :on-success="onUploadSuccess"
          :on-remove="onRemoveLogo"
        >
          <el-icon><Plus /></el-icon>
        </el-upload>
        <div class="tip">{{ t('brandSetting.logoTip') }}</div>
      </el-form-item>
      <el-form-item :label="t('brandSetting.themeColor')">
        <div class="color-section">
          <div class="preset-label">{{ t('brandSetting.recommended') }}</div>
          <div class="color-presets">
            <div
              v-for="item in RECOMMENDED_THEME_COLORS"
              :key="item.value"
              class="preset-item"
              :class="{ active: isActiveColor(item.value) }"
              :title="item.name"
              @click="selectPreset(item.value)"
            >
              <span class="preset-swatch" :style="{ backgroundColor: item.value }" />
              <span class="preset-name">{{ item.name }}</span>
            </div>
          </div>
          <div class="color-picker-row">
            <el-color-picker
              v-model="state.form.backendThemeColor"
              :show-alpha="false"
              @active-change="onColorActiveChange"
            />
            <el-input
              v-model="state.form.backendThemeColor"
              class="color-input"
              placeholder="#f4f5fa"
              @input="onColorInput"
            />
          </div>
          <div class="theme-preview" :style="{ backgroundColor: previewColor }">
            <div class="preview-brand">
              <img v-if="state.form.backendLogoUrl" :src="state.form.backendLogoUrl" class="preview-logo" alt="" />
              <span class="preview-title">{{ state.form.backendSystemName || t('brandSetting.previewTitle') }}</span>
            </div>
            <span class="preview-hint">{{ t('brandSetting.headerPreview') }}</span>
          </div>
        </div>
      </el-form-item>
      <el-form-item :label="t('brandSetting.remark')">
        <el-input
          v-model="state.form.backendBrandRemark"
          type="textarea"
          :rows="3"
          :placeholder="t('brandSetting.remarkPh')"
          @input="onBrandTextChange"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="submit">{{ t('brandSetting.save') }}</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script setup>
import { reactive, onMounted, computed } from "vue";
import { ElMessage } from "element-plus";
import { Plus } from "@element-plus/icons-vue";
import { getFormApi, saveApi } from "@/api/system/setting";
import { globalConfig } from "@/configs/globalConfig";
import {
  DEFAULT_THEME,
  RECOMMENDED_THEME_COLORS,
  applyBackendBranding,
  normalizeThemeColor,
} from "@/utils/tenantBranding";
import { usePageI18n } from "@/composables/usePageI18n.js";

const applyLabels = () => {};
const { t } = usePageI18n(applyLabels);

const state = reactive({
  loading: true,
  uploadUrl: globalConfig.uploadUrl,
  headers: {
    Authorization: "Bearer " + localStorage.getItem("token"),
  },
  form: {
    backendSystemName: "",
    backendLogoUrl: "",
    backendThemeColor: DEFAULT_THEME,
    backendBrandRemark: "",
  },
  logoList: [],
});

const previewColor = computed(() =>
  normalizeThemeColor(state.form.backendThemeColor)
);

const isActiveColor = (value) =>
  previewColor.value.toLowerCase() === normalizeThemeColor(value).toLowerCase();

const pushBrandPreview = () => {
  applyBackendBranding({
    backendSystemName: state.form.backendSystemName,
    backendLogoUrl: state.form.backendLogoUrl,
    backendThemeColor: state.form.backendThemeColor,
    backendBrandRemark: state.form.backendBrandRemark,
  });
};

const applyThemeNow = (color) => {
  state.form.backendThemeColor = normalizeThemeColor(color);
  pushBrandPreview();
};

const selectPreset = (color) => {
  applyThemeNow(color);
};

const onColorActiveChange = (color) => {
  if (color) {
    applyThemeNow(color);
  }
};

const onColorInput = (val) => {
  applyThemeNow(val);
};

const onBrandTextChange = () => {
  pushBrandPreview();
};

const syncBrandCache = () => {
  pushBrandPreview();
};

const getForm = () => {
  state.loading = true;
  getFormApi("{}").then((res) => {
    const data = res?.data || {};
    state.form.backendSystemName = data.backendSystemName || "";
    state.form.backendLogoUrl = data.backendLogoUrl || "";
    state.form.backendThemeColor = normalizeThemeColor(
      data.backendThemeColor || DEFAULT_THEME
    );
    state.form.backendBrandRemark = data.backendBrandRemark || "";
    state.logoList = state.form.backendLogoUrl
      ? [{ name: "logo", url: state.form.backendLogoUrl }]
      : [];
    syncBrandCache();
    state.loading = false;
  });
};

const beforeUpload = (file) => {
  const isImage = file.type.startsWith("image/");
  if (!isImage) {
    ElMessage.error(t("brandSetting.imageOnly"));
    return false;
  }
  return true;
};

const onUploadSuccess = (response) => {
  const url = response?.data || response;
  if (!url) return;
  state.form.backendLogoUrl = url;
  pushBrandPreview();
  ElMessage.success(t("brandSetting.logoUploaded"));
};

const onRemoveLogo = () => {
  state.form.backendLogoUrl = "";
  pushBrandPreview();
};

const submit = () => {
  if (state.logoList.length > 0) {
    const first = state.logoList[0];
    state.form.backendLogoUrl = first.response?.data || first.url || "";
  }
  const theme = normalizeThemeColor(state.form.backendThemeColor);
  state.form.backendThemeColor = theme;
  const params = JSON.stringify({
    backend_system_name: state.form.backendSystemName,
    backend_logo_url: state.form.backendLogoUrl,
    backend_theme_color: theme,
    backend_brand_remark: state.form.backendBrandRemark,
  });
  saveApi(params).then((res) => {
    if (res?.code === 200) {
      syncBrandCache();
      ElMessage.success(t("brandSetting.saveSynced"));
    } else {
      ElMessage.error(res?.msg || t("brandSetting.saveFailed"));
    }
  });
};

onMounted(() => {
  applyLabels();
  getForm();
});
</script>

<style scoped lang="less">
.finance_box {
  flex: 1;
  overflow: auto;
  background-color: #fff;
  border-radius: 6px;
  padding: 20px;
  margin-right: 10px;
}
.brand-form {
  max-width: 820px;
}
.color-section {
  width: 100%;
}
.preset-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
}
.color-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-bottom: 12px;
  max-width: 520px;
}
.preset-item {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  padding: 4px 5px;
  border-radius: 6px;
  border: 1px solid transparent;
  transition: border-color 0.15s, background-color 0.15s;
  &:hover {
    border-color: #c6d4f7;
    background-color: #f5f8ff;
  }
  &.active {
    border-color: #409eff;
    background-color: #ecf5ff;
  }
}
.preset-swatch {
  width: 32px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}
.preset-name {
  font-size: 10px;
  color: #666;
  line-height: 1.2;
  max-width: 36px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.color-picker-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.color-input {
  width: 220px;
}
.theme-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 52px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px dashed #dcdfe6;
  transition: background-color 0.15s ease;
}
.preview-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.preview-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}
.preview-title {
  font-size: 16px;
  line-height: 20px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.preview-hint {
  flex-shrink: 0;
  font-size: 12px;
  color: #909399;
}
.tip {
  margin-top: 6px;
  color: #888;
  font-size: 12px;
}
</style>
